import React, { FC, useMemo } from 'react';
import { Chip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Styles from '@/assets/js/Styles';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import { PageStatusCircle } from '@/components/PageStatusCircle';
import CustomIcon from '@/components/CustomIcon';
import { formatAPITimestamp, isApiTimestampAtOrAfter } from '@/utils/dates';
import { Page } from '@/declarations/models/Page';
import { Status } from '@/declarations/models/Status';
import { MainCategory } from '@/declarations/Category';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LockIcon from '@mui/icons-material/Lock';
import M24PageFinderPageTreeLabel from './M24PageFinderPageTreeLabel';
import Container from '../../../Container';
import { useM24PageFinderContentView } from '../M24PageFinder';
import Image from '../../../Image';

export interface PageTreeContentProps {
  page: Page;
  diffuse?: boolean;
  selected?: boolean;
}

type DisplayStatus = Status | 'planned_publish';

const getDisplayStatus = (page: Page): DisplayStatus => {
  if (page.status === Status.PUBLISHED) {
    const isPlannedPublish = !!page.published_at && isApiTimestampAtOrAfter(page.published_at);

    if (isPlannedPublish) {
      return 'planned_publish';
    }
  }

  return page.status;
};

const getDisplayStatusText = (displayStatus: DisplayStatus, tCommon: (key: string) => string): string => {
  if (displayStatus === 'planned_publish') {
    return tCommon('Status.planned_publishing');
  }

  if (displayStatus === Status.PUBLISHED) {
    return tCommon('Status.published');
  }

  if (displayStatus === Status.DRAFT) {
    return tCommon('Status.not_published');
  }

  if (displayStatus === Status.ARCHIVED) {
    return tCommon('Status.archived');
  }

  return '';
};

export const M24PageFinderPageTreeContent: FC<PageTreeContentProps> = ({ page, diffuse, selected }) => {
  const { t } = useTranslation('common');
  const { onSelect } = useM24PageFinderContentView();
  const displayStatus = useMemo(() => getDisplayStatus(page), [page]);
  const pageStatusText = useMemo(() => getDisplayStatusText(displayStatus, t), [displayStatus, t]);

  let statusColor: string;

  if (displayStatus === 'planned_publish') {
    statusColor = Styles.Colors.STRONG_ORANGE;
  } else if (displayStatus === Status.PUBLISHED) {
    statusColor = 'green';
  } else {
    statusColor = 'textSecondary';
  }

  return (
    <Container
      role='link'
      onClick={(e) => {
        e.stopPropagation();
        onSelect(page);
      }}
      sx={{
        opacity: diffuse ? 0.5 : 1,
        height: '48px',
        cursor: 'pointer',
        py: 4,
      }}
      spaceBetween
      fullWidth>
      <Container fullWidth spaceBetween>
        <M24PageFinderPageTreeLabel page={page} />
        <Container right mr={1}>
          {!!page?.main_category && page.main_category !== MainCategory.EVENT && (
            <Chip variant='outlined' color='secondary' label={t(`MainCategory.${page.main_category}`)} size='small' />
          )}
          {page?.sub_categories?.slice(0, 1).map((c) => (
            <Chip key={c} variant='outlined' color='secondary' label={t(`SubCategory.${c}`)} size='small' />
          ))}
          {page.sub_categories && page.sub_categories?.length > 1 ? (
            <Chip
              variant='outlined'
              color='secondary'
              size='small'
              label={`+ ${(page.sub_categories?.length || 1) - 1}`}
            />
          ) : null}
        </Container>
      </Container>

      <Container>
        {page.is_protected === true && (
          <Container>
            <LockIcon
              sx={{
                fontSize: '14px',
                color: page.locked_by_parent ? 'text.disabled' : 'inherit',
                opacity: page.locked_by_parent ? 0.7 : 1,
              }}
            />
          </Container>
        )}
        {page.status === Status.PUBLISHED && !!page.published_at && isApiTimestampAtOrAfter(page.published_at) && (
          <Container>
            <AccessTimeIcon />
          </Container>
        )}
        <Container spaceBetween sx={{ minWidth: '320px' }}>
          <Container column left sx={{ minWidth: '220px' }} gap={0}>
            <Container>
              <PageStatusCircle status={displayStatus} size={18} />
              <Typography variant='caption' color={statusColor}>
                {pageStatusText}
              </Typography>
            </Container>
            <Container column left gap={0}>
              <Typography variant='caption' color='textSecondary' noWrap>
                {t('lastEdited')}: {formatAPITimestamp(page.updated_at || '', 'date') || '-'}
              </Typography>
              <Typography variant='caption' color='textSecondary' noWrap>
                {t('publishDate')}: {formatAPITimestamp(page.published_at || '', 'date') || '-'}
              </Typography>
            </Container>
          </Container>
          <Container>
            <Image
              sx={{
                overflow: 'hidden',
                objectFit: 'cover',
                height: '40px',
                width: '40px',
                borderRadius: Styles.Dimensions.RADIUS_ROUND_FULL,
                backgroundColor: Styles.Colors.LIGHT_GREY,
                border: `1px solid ${Styles.Colors.MEDIUM_LIGHT_GREY}`,
              }}
              src={`${page.image_src}?&dimension=250x250${
                page.image?.mimetype?.includes('png') ? '&mediaType=image/png' : ''
              }`}
            />
          </Container>
        </Container>
      </Container>

      <Container sx={{ minWidth: '24px' }}>{selected && <MaterialSymbol name='check' color='info' />}</Container>
    </Container>
  );
};

export default M24PageFinderPageTreeContent;
